import clsx from 'clsx'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'

import SummaryCardImage from '../SummaryCardImage'

import { track } from 'analytics/analytics'
import { viewMoreDetailsExploreItemEvent } from 'analytics/snowplow/events'
import IconButton from 'components/Luxkit/Button/IconButton'
import TextButton from 'components/Luxkit/Button/TextButton'
import SolidTimesIcon from 'components/Luxkit/Icons/solid/SolidTimesIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import HeadingTextBlock from 'components/Luxkit/TextBlocks/HeadingTextBlock'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import ExploreBookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/Explore/ExploreBookmarkButton'
import ExploreBookmarkToItineraryButton from 'tripPlanner/components/Bookmark/BookmarkButton/Explore/ExploreBookmarkToItineraryButton'
import GooglePlaceRatingDisplay from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRatingDisplay'
import ExploreItemDetails from 'tripPlanner/components/TripModal/ExploreItems/ExploreItemDetails'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import { ExploreItem } from 'tripPlanner/types/common'
import { cardItemHoverAnimation } from 'tripPlanner/utils/css'

const CardContainer = styled.div`
  display: flex;
  justify-content: space-around;
`

const Container = styled.div`
  position: relative;
  width: 100%;
  background-color: white;
  max-width: ${rem(300)};

  &.alwaysShadow {
    box-shadow: ${({ theme }) => theme.shadow.bottom.medium};
  }

  &:not(.alwaysShadow) {
    ${cardItemHoverAnimation}
  }

  ${mediaQueryUp.largeDesktop} {
    max-width: ${rem(700)};
  }
`

const ImageContainer = styled.div`
  width: 100%;

  > * {
    height: 100%;
    aspect-ratio: 16/9;
  }

  ${mediaQueryUp.largeDesktop} {
    width: 33%;
  }
`
const ContentWrapper = styled.div`
  padding: ${rem(16)};
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: start;
  gap: ${rem(8)};

  ${mediaQueryUp.largeDesktop} {
    padding: ${rem(24)};
  }
`

const MainGroup = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  ${mediaQueryUp.largeDesktop} {
    flex-direction: row-reverse;
    justify-content: space-between;
  }
`

const CloseButton = styled(IconButton)`
  position: absolute;
  bottom: calc(100% + ${rem(16)});
  right: 0;
  z-index: 9999;
  box-shadow: ${(props) => props.theme.shadow.flat.large};
`

interface Props {
  className?: string
  item: ExploreItem
  /** If true, the card will always have a shadow, even when not hovered */
  alwaysShadow?: boolean
  showCloseButton?: boolean
  onClose?: () => void
}

const BookmarkButtonContainer = styled(Group)`
  position: absolute;
  top: ${rem(12)};
  right: ${rem(12)};
`

const ExploreSummaryCard = React.forwardRef<HTMLDivElement, Props>(
  ({ className, item, alwaysShadow, showCloseButton, onClose }, ref) => {
    const { place, name, rating, userRatingCount } = item
    const { region } = place

    const showModal = useContext(ModalContext)

    const tripId = useTripId()

    const openDetailsModal = useCallback(() => {
      showModal(<ExploreItemDetails item={item} tripId={tripId} />)

      track(
        'click-explore-view-more-details-trip-planner',
        viewMoreDetailsExploreItemEvent(),
      )
    }, [item, tripId, showModal])

    return (
      <CardContainer className={className}>
        <Container ref={ref} className={clsx({ alwaysShadow })}>
          {showCloseButton && (
            <CloseButton
              kind="primary"
              variant="ghost"
              size="medium"
              shape="circle"
              aria-label="Close"
              onClick={onClose}
            >
              <SolidTimesIcon />
            </CloseButton>
          )}
          <MainGroup>
            <ImageContainer>
              <SummaryCardImage
                place={place}
                tripId={tripId}
                tripItemId={item.id}
              />
            </ImageContainer>
            <ContentWrapper>
              <Group direction="vertical" gap={8}>
                <BodyTextBlock variant="small">{region}</BodyTextBlock>

                <HeadingTextBlock lineClamp={2} variant="heading6">
                  {name}
                </HeadingTextBlock>

                {rating && (
                  <GooglePlaceRatingDisplay
                    rating={rating}
                    ratingsCount={userRatingCount}
                    size="S"
                  />
                )}
              </Group>

              <TextButton
                kind="tertiary"
                size="small"
                variant="default"
                outdent="left"
                nonInteractable
                onClick={openDetailsModal}
              >
                View details
              </TextButton>
            </ContentWrapper>
          </MainGroup>
          <BookmarkButtonContainer
            direction="horizontal"
            horizontalAlign="end"
            gap={8}
          >
            <ExploreBookmarkToItineraryButton
              exploreItem={item}
              variant="chip"
            />
            <ExploreBookmarkButton exploreItem={item} variant="icon" />
          </BookmarkButtonContainer>
        </Container>
      </CardContainer>
    )
  },
)

export default ExploreSummaryCard
